import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { updateProfile, updateProfiles } from 'flow/actions/profile-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import ProfileToggleItem from 'flow/components/settings/call-recording/ProfileToggleItem';
import Loading from 'flow/components/ui-components/Loading';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getProfileDTOWithValue,
    monitoringVaries,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';

const MonitoringSettings = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { profiles, getProfile } = useProfiles(extensionKey);

    const monitoringVariesPerProfile = monitoringVaries(profiles);

    const [editPerProfile, setEditPerProfile] = useState(
        monitoringVariesPerProfile,
    );

    useEffect(() => {
        setEditPerProfile(monitoringVariesPerProfile);
    }, [profiles]);

    if (!profiles?.size) {
        return <Loading />;
    }

    const resetAllProfiles = () => {
        const availableProfileSetting = profiles
            .first()
            .get('canBeCoMonitored');
        updateAllProfiles(availableProfileSetting);
        setEditPerProfile(false);
    };

    const updateAllProfiles = (value: boolean) => {
        const updatedProfiles = profiles.map((profileDTO) =>
            getProfileDTOWithValue(profileDTO, ['canBeCoMonitored'], value),
        );
        dispatch(updateProfiles(extensionKey, updatedProfiles));
    };

    const updateSingleProfile = (
        profileDTO: TypedMap<Profile>,
        value: boolean,
    ) => {
        const updatedProfile = getProfileDTOWithValue(
            profileDTO,
            ['canBeCoMonitored'],
            value,
        );
        dispatch(updateProfile(extensionKey, updatedProfile));
    };

    const handleToggle = (profileKey: string, value: boolean) => {
        if (profileKey === 'all_profiles') {
            updateAllProfiles(value);
        } else {
            const profileDTO = getProfile(profileKey);
            updateSingleProfile(profileDTO, value);
        }
    };

    const confirmDescription = (
        <div
            dangerouslySetInnerHTML={{
                __html: i18next.t('settings.reset.warning.general', {
                    currentSettings: i18next.t('settings.call_monitoring'),
                    firstProfileName: profiles.first().get('description'),
                }),
            }}
        />
    );

    const handleToggleEditByProfile = (value: boolean) => {
        if (monitoringVariesPerProfile && !value) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.reset.warning.general.Title'),
                description: confirmDescription,
                handleAccept: resetAllProfiles,
            });
        } else {
            setEditPerProfile(value);
        }
    };

    const toggledProfileKeys = profiles
        .filter((profile) => profile.get('canBeCoMonitored'))
        .map((profile) => profile.get('key'));

    const footer = (
        <>
            <p>{i18next.t('settings.profile_edit')}</p>
            <Toggle
                value={editPerProfile}
                onChange={handleToggleEditByProfile}
                text={i18next.t('settings.profile_edit')}
            />
        </>
    );

    return (
        <Page
            header={'settings.call_monitoring'}
            footer={footer}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['settings.call-recording-and-monitoring'],
                        {
                            extensionKey,
                        },
                    ),
                )
            }
        >
            {editPerProfile ? (
                <>
                    {profiles.map((profile) => (
                        <ProfileToggleItem
                            key={profile.get('key')}
                            profile={profile}
                            profileState={toggledProfileKeys.includes(
                                profile.get('key'),
                            )}
                            handleToggle={handleToggle}
                        />
                    ))}
                </>
            ) : (
                <SettingRow
                    cta={
                        <Toggle
                            value={profiles.first().get('canBeCoMonitored')}
                            onChange={updateAllProfiles}
                            text={i18next.t('settings.allow_call_monitoring')}
                        />
                    }
                >
                    {i18next.t('settings.allow_call_monitoring')}
                </SettingRow>
            )}
        </Page>
    );
};

export default MonitoringSettings;
